import type { AxiosError } from 'axios'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

import { normalizeUser } from '@/lib/adapters/user.adapter'
import { useErrorHandler } from '@/lib/hooks/error.hooks'
import { request } from '@/lib/services'
import type { UserType } from '@/lib/types/user.types'

export const useUserStore = defineStore('user', () => {
  const user = ref<UserType>()
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const legacyStore = useStore()
  const { handleError } = useErrorHandler()
  const isInitialized = ref(false)

  const initialize = async () => {
    isLoading.value = true
    error.value = null
    try {
      const response = await request.get('/user/info')
      user.value = normalizeUser(response.data)

      await legacyStore.dispatch('user/setUserFromAppPinia', user.value, { root: true })
    } catch (err) {
      error.value = err as Error
      await handleError({
        title: 'Authentication Failed',
        message:
          'We encountered an error while loading user data. Please try again or contact support if the problem persists.',
        error: error.value,
      })
    } finally {
      isLoading.value = false
      isInitialized.value = true
    }
  }

  const handleAdvertiserInvitation = async (): Promise<boolean> => {
    try {
      await request.patch('/v2/me/advertiser-invites', {}, { isForcedRetryOnError: true })

      return true
    } catch (err) {
      if ((err as AxiosError).response?.status === 404) {
        return false
      }
      throw err
    }
  }

  const name = computed(() => (user.value ? user.value.username : ''))
  const subscriberId = computed(() => (user.value ? user.value.subscriber?.id : ''))
  const userAdvertisers = computed(() => user.value?.advertisers || [])
  const id = computed(() => user.value?.id)
  const isAdmin = computed(() => Boolean(user.value?.admin))

  return {
    id: id as unknown as number,
    user: user as unknown as UserType,
    isAdmin: isAdmin as unknown as boolean,
    name: name as unknown as string,
    userAdvertisers: userAdvertisers,
    subscriberId: subscriberId as unknown as string,
    isLoading,
    error,
    initialize,
    handleAdvertiserInvitation,
    isInitialized,
  }
})
